'use client';

import dynamic from 'next/dynamic';
import {
  cloneElement,
  useState,
  type FocusEventHandler,
  type MouseEventHandler,
  type PointerEventHandler,
  type ReactElement,
} from 'react';
import { pushApplyStart } from '@/lib/gtm';
import type { JobDetail } from '@/services/jobs/model';
import { SOURCE_SCREENS } from '@/services/opportunity-tracking/source-screen';
import type { ApplicationSourceScreen } from './application-dialog-types';

type TriggerProps = {
  disabled?: boolean;
  onClick?: MouseEventHandler;
  onFocus?: FocusEventHandler;
  onPointerEnter?: PointerEventHandler;
};

type LazyJobApplicationDialogProps = {
  job: JobDetail;
  jobId: string;
  jobTitle: string;
  onApplied?: () => void;
  sourceScreen: ApplicationSourceScreen;
  trigger: ReactElement<TriggerProps>;
};

function loadApplicationDialog() {
  return import('./job-application-dialog');
}

const JobApplicationDialog = dynamic(
  () => loadApplicationDialog().then(module => module.JobApplicationDialog),
  { ssr: false }
);

export function LazyJobApplicationDialog({
  job,
  jobId,
  jobTitle,
  onApplied,
  sourceScreen,
  trigger,
}: LazyJobApplicationDialogProps) {
  const [isActivated, setIsActivated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleApplyStart: MouseEventHandler = event => {
    trigger.props.onClick?.(event);

    if (event.defaultPrevented) return;

    pushApplyStart(
      job,
      sourceScreen === SOURCE_SCREENS.JOB_DETAIL ? 'job_detail' : 'job_list'
    );
  };

  if (isActivated) {
    return (
      <JobApplicationDialog
        defaultOpen
        job={job}
        jobId={jobId}
        jobTitle={jobTitle}
        onApplied={onApplied}
        sourceScreen={sourceScreen}
        trigger={cloneElement(trigger, { onClick: handleApplyStart })}
      />
    );
  }

  const preload = () => {
    void loadApplicationDialog();
  };

  return cloneElement(trigger, {
    disabled: trigger.props.disabled || isLoading,
    onClick: async event => {
      handleApplyStart(event);

      if (event.defaultPrevented) return;

      setIsLoading(true);

      try {
        await loadApplicationDialog();
        setIsActivated(true);
      } catch (error) {
        console.error('[JobApplicationDialog] Unable to load dialog', error);
      } finally {
        setIsLoading(false);
      }
    },
    onFocus: event => {
      trigger.props.onFocus?.(event);
      preload();
    },
    onPointerEnter: event => {
      trigger.props.onPointerEnter?.(event);
      preload();
    },
  });
}
